"use client";

import { c as _c } from "react/compiler-runtime";
import { Image } from "@opensea/next-ui-kit";
import { CenterAligned, classNames, Flex, FlexColumn, TextBody, TextHeading } from "@opensea/ui-kit";
import { useTranslations } from "next-intl";
import { COMPLETE_VOYAGES, DISCOVER_TREASURES, EARN_SHIPMENTS, STAY_LOYAL, TREASURE_0, TREASURE_1, TREASURE_2, TREASURE_3, TREASURE_4, TREASURE_5, TREASURE_6 } from "@/constants/assets";
import { Sound, usePlaySound } from "@/hooks/use-play-sound";
import { OnboardingButton } from "../components/OnboardingButton";
import { StepContainer } from "./Step";
function RewardsStepCard(t0) {
  const $ = _c(16);
  const {
    title,
    description,
    icon,
    iconClassName
  } = t0;
  let t1;
  if ($[0] !== iconClassName) {
    t1 = classNames("aspect-auto h-auto w-full", iconClassName);
    $[0] = iconClassName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon.src || $[3] !== t1 || $[4] !== title) {
    t2 = <CenterAligned className="relative aspect-square size-25 min-w-25 rounded-lg border border-border-1 bg-bg-secondary p-3 md:size-30 md:min-w-30 md:p-6"><Image alt={title} className={t1} fill={true} src={icon.src} /></CenterAligned>;
    $[2] = icon.src;
    $[3] = t1;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== title) {
    t3 = <TextHeading size="sm">{title}</TextHeading>;
    $[6] = title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== description) {
    t4 = <TextBody color="text-secondary">{description}</TextBody>;
    $[8] = description;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <FlexColumn className="gap-2 text-left">{t3}{t4}</FlexColumn>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t5) {
    t6 = <Flex className="gap-5 md:gap-8 lg:items-center">{t2}{t5}</Flex>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
const baseMap = {
  0: {
    delay: 0
  },
  1: {
    delay: 1
  },
  2: {
    delay: 2
  },
  3: {
    delay: 2
  },
  4: {
    delay: 1
  },
  5: {
    delay: 3
  },
  6: {
    delay: 3
  }
};
const offsetMap = {
  0: {
    scale: 1,
    x: 0,
    y: -115
  },
  // Center
  1: {
    scale: 0.5,
    x: 125,
    y: -200
  },
  // Right
  2: {
    scale: 1.4,
    x: -350,
    y: -125
  },
  // Left
  3: {
    scale: 1.05,
    x: 250,
    y: -100
  },
  // Right
  4: {
    scale: 0.75,
    x: -150,
    y: -190
  },
  // Left
  5: {
    scale: 1.25,
    x: 450,
    y: -165
  },
  // Right
  6: {
    scale: 0.75,
    x: -525,
    y: -200
  } // Left
};
const ROTATION_OFFSET = 25;
function getRotationOffset(x) {
  if (x > 0) {
    return ROTATION_OFFSET;
  }
  if (x < 0) {
    return -ROTATION_OFFSET;
  }
  return 0;
}
const rotationOffsetMap = Object.fromEntries(Object.entries(offsetMap).map(([k, v]) => [k, {
  rotate: getRotationOffset(v.x)
}]));
function RewardsStepHeader(t0) {
  const $ = _c(3);
  const {
    presented
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      src: TREASURE_0
    }, {
      src: TREASURE_1
    }, {
      src: TREASURE_2
    }, {
      src: TREASURE_3
    }, {
      src: TREASURE_4
    }, {
      src: TREASURE_5
    }, {
      src: TREASURE_6
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const images = t1;
  let t2;
  if ($[1] !== presented) {
    t2 = <CenterAligned className="z-0 h-12 w-full translate-y-18 scale-[0.65] md:h-48 md:translate-y-30 md:scale-[0.75]">{images.map((image, index) => {
        const style = {
          ...(presented ? {
            rotate: "0deg",
            transitionDelay: `${((3 - baseMap[index].delay) * 0.1).toString()}s`,
            "--tw-scale-x": offsetMap[index].scale.toString(),
            "--tw-scale-y": offsetMap[index].scale.toString(),
            "--tw-scale-z": offsetMap[index].scale.toString(),
            "--tw-translate-x": `${offsetMap[index].x.toString()}px`,
            "--tw-translate-y": `${offsetMap[index].y.toString()}px`
          } : {
            rotate: `${rotationOffsetMap[index].rotate.toString()}deg`
          })
        };
        return <div className={classNames("absolute z-0 aspect-auto h-auto w-32 scale-100", "translate-0 scale-0", "transition-[scale,rotate,translate] duration-1000 ease-out-quint")} key={index} style={style}><Image alt={`Treasure ${index}`} className="w-full animate-wiggle" fill={true} src={image.src} /></div>;
      })}</CenterAligned>;
    $[1] = presented;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function RewardsStep(props) {
  const $ = _c(70);
  const t = useTranslations("RewardsStep");
  const [playSelect] = usePlaySound(Sound.Select);
  let t0;
  if ($[0] !== playSelect || $[1] !== props) {
    t0 = async () => {
      playSelect();
      props.onConfirm();
    };
    $[0] = playSelect;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleGameOn = t0;
  const t1 = props.currentIndex >= props.index;
  let t2;
  if ($[3] !== t1) {
    t2 = <RewardsStepHeader presented={t1} />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("heading");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <TextHeading size="lg">{t3}</TextHeading>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("subheading");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <TextBody color="text-secondary">{t5}</TextBody>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <FlexColumn className="items-center gap-3">{t4}{t6}</FlexColumn>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("gameOn");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== handleGameOn || $[19] !== t8) {
    t9 = <OnboardingButton className="w-1/2" onClick={handleGameOn} variant="primary">{t8}</OnboardingButton>;
    $[18] = handleGameOn;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t9) {
    t10 = <FlexColumn className="items-center gap-6">{t7}{t9}</FlexColumn>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t2) {
    t11 = <FlexColumn className="relative items-center gap-12 pt-16">{t2}{t10}</FlexColumn>;
    $[24] = t10;
    $[25] = t2;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("completeVoyages.description");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      src: COMPLETE_VOYAGES
    };
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("completeVoyages.title");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14) {
    t15 = <RewardsStepCard description={t12} icon={t13} iconClassName="scale-80 md:scale-100" title={t14} />;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("discoverTreasures.description");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      src: DISCOVER_TREASURES
    };
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("discoverTreasures.title");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t16 || $[41] !== t18) {
    t19 = <RewardsStepCard description={t16} icon={t17} iconClassName="md:scale-120" title={t18} />;
    $[40] = t16;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("earnShipments.description");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      src: EARN_SHIPMENTS
    };
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("earnShipments.title");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t20 || $[49] !== t22) {
    t23 = <RewardsStepCard description={t20} icon={t21} iconClassName="scale-90 md:scale-110" title={t22} />;
    $[48] = t20;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t("loyaltyAndIntegrity.description");
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      src: STAY_LOYAL
    };
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t) {
    t26 = t("loyaltyAndIntegrity.title");
    $[54] = t;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t24 || $[57] !== t26) {
    t27 = <RewardsStepCard description={t24} icon={t25} iconClassName="scale-65 md:scale-80" title={t26} />;
    $[56] = t24;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t15 || $[60] !== t19 || $[61] !== t23 || $[62] !== t27) {
    t28 = <FlexColumn className="gap-5 md:w-1/2">{t15}{t19}{t23}{t27}</FlexColumn>;
    $[59] = t15;
    $[60] = t19;
    $[61] = t23;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t11 || $[65] !== t28) {
    t29 = <FlexColumn className="absolute top-[62px] size-full md:top-0"><FlexColumn className="relative z-10 items-center gap-8 px-6 pb-12 md:px-0 md:pb-24">{t11}{t28}</FlexColumn></FlexColumn>;
    $[64] = t11;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== props || $[68] !== t29) {
    t30 = <StepContainer {...props}>{t29}</StepContainer>;
    $[67] = props;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  return t30;
}